<script setup lang="ts">
import type { RecuFiscalPays } from "@donoor/utils";
import {
  cleConditionEconomie,
  coutAuConditionnel,
  tauxEconomieMaxDuRegime,
  tauxReductionDuRegime,
  useRegimeFiscal,
} from "~/composables/useRegimeFiscal";
/**
 * Réduction d'impôt en version DISCRÈTE (carte de don de la page cagnotte,
 * Karim 2026-10-13) : « Soit 17 € après réduction fiscale » + « ✓ Reçu
 * fiscal inclus », à la place de l'encart noir des pages de paiement
 * (`DonateFiscalBanner`, qui reste tel quel ailleurs).
 *
 * Même calcul que `DonateFiscalBanner` : 66 % du DON SEUL (art. 200 CGI),
 * hors soutien Donoor et frais couverts ; le taux 75 % n'est pas transmis au
 * site, on ne promet donc jamais plus que la réalité.
 *
 * Le montant DÉFILE (Karim 2026-10-13) : de 0 à l'arrivée sur la page, puis
 * de l'ancien au nouveau à chaque changement de montant. Seuls les EUROS
 * défilent, sans virgule ; les centimes apparaissent à la fin. Aucun
 * défilement si l'appareil demande de réduire les animations.
 */
import { useAnimatedNumber } from "~/composables/useAnimatedNumber";

const props = defineProps<{
  /** Montant du DON seul (hors soutien Donoor et frais couverts). */
  finalAmount: number;
  formatAmount: (amount: number) => string;
}>();

// Belgique : 30 %. Luxembourg et Pays-Bas : déduction du revenu → « Soit X €
// après déduction fiscale » au taux le plus favorable, la condition en petit
// dessous (Karim, 01/11/2026). Allemagne (03/11/2026) : « Peut ne vous
// coûter que X € après déduction fiscale », jamais une économie certaine.
const regime = useRegimeFiscal();
const taux = computed(() => tauxReductionDuRegime(regime.value));
const tauxMax = computed(() => (taux.value === null ? tauxEconomieMaxDuRegime(regime.value) : null));
const condition = computed(() => (tauxMax.value !== null ? cleConditionEconomie(regime.value) : null));
// Aucun montant (ou montant invalide) → « Soit 0 € », jamais une ligne vide.
const coutApresReduction = computed(() => {
  const t = taux.value ?? tauxMax.value;
  return props.finalAmount > 0 && t !== null ? Math.round(props.finalAmount * (1 - t) * 100) / 100 : 0;
});
// Un texte par régime : un nouveau pays oublié ici ne compile pas.
const MENTIONS_RECU: Record<RecuFiscalPays, string> = {
  cerfa: "don.optionsGroupe.fiscalRecuInclus",
  be_281_71: "recusPays.fiscalRecuInclusBe",
  lu_art112: "recusPays.fiscalRecuInclusLu",
  nl_anbi: "recusPays.fiscalRecuInclusNl",
  de_zuwendung: "recusPays.fiscalRecuInclusDe",
};
const mentionRecu = computed(() => MENTIONS_RECU[regime.value] ?? MENTIONS_RECU.cerfa);
// Sans taux (déduction du revenu) : le titre du pays.
const titreSansTaux = computed(() =>
  regime.value === "nl_anbi"
    ? "recusPays.fiscalTitreNl"
    : regime.value === "de_zuwendung"
      ? "recusPays.fiscalTitreDe"
      : "recusPays.fiscalTitreLu",
);
const phraseCout = computed(() =>
  taux.value !== null
    ? "don.optionsGroupe.fiscalSoit"
    : coutAuConditionnel(regime.value)
      ? "recusPays.fiscalSoitPeutDeduction"
      : "recusPays.fiscalSoitDeduction",
);

const { displayValue: eurosAffiches, setValue } = useAnimatedNumber(0, 700);
// Pendant le défilement : euros entiers ; une fois arrivé : montant exact.
const montantAffiche = computed(() => {
  const cout = coutApresReduction.value;
  return eurosAffiches.value === Math.floor(cout) ? cout : eurosAffiches.value;
});

function animerVers(cout: number) {
  const reduire =
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  setValue(Math.floor(cout), !reduire);
}
// Arrivée sur la page : défilement depuis 0 (côté navigateur seulement).
onMounted(() => animerVers(coutApresReduction.value));
watch(coutApresReduction, animerVers);
</script>

<template>
  <!-- Police du site (Plus Jakarta Sans) imposée : chargée en 400 à 700. -->
  <div class="border-y border-gray-200 py-4 text-center" style="font-family: var(--font-body) !important">
    <p v-if="taux === null && tauxMax === null" class="text-[15px] leading-snug text-(--color-ink)" style="font-weight: 600 !important">
      {{ $t(titreSansTaux) }}
    </p>
    <i18n-t
      v-else
      :keypath="phraseCout"
      tag="p"
      class="text-[15px] leading-snug text-(--color-ink)"
    >
      <template #cout>
        <!-- Chiffres à chasse fixe : la largeur ne tremble pas pendant le défilement. -->
        <span class="whitespace-nowrap text-[17px] tabular-nums" style="font-weight: 700 !important">{{ formatAmount(montantAffiche) }}</span>
      </template>
    </i18n-t>
    <p v-if="condition" class="mt-1 text-[12.5px] leading-snug text-gray-500">{{ $t(condition) }}</p>
    <p
      class="mt-1.5 flex items-center justify-center gap-1.5 text-[13px] text-gray-500"
    >
      <UIcon name="i-lucide-check" class="size-3.5 shrink-0" />
      {{ $t(mentionRecu) }}
    </p>
  </div>
</template>
